/**
 * Composant de gestion des sessions
 * RÉUTILISABILITÉ : 100% - Compatible Next.js et React
 *
 * Liste et gestion des sessions actives avec révocation.
 */

  // Client Component pour Next.js

import { useEffect, useState } from 'react';
import { useSession } from '../../hooks';
import { SessionCard } from '../ui/SessionCard';
import { SecurityAlert, useSecurityAlert } from '../ui/SecurityAlert';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { useNavigate } from 'react-router-dom';
import { tokenManager } from '../../../infrastructure/services/tokenManager';
/**
 * Props du composant SessionsManager
 */
export interface SessionsManagerProps {
  /** ID de l'utilisateur */
  userId: string;
  /** Callback après révocation de session */
  onSessionRevoked?: (sessionId: string) => void;
  /** Callback après révocation de toutes les sessions */
  onAllSessionsRevoked?: () => void;
  /** Afficher le bouton de révocation globale */
  showRevokeAll?: boolean;
  /** Classes CSS additionnelles */
  className?: string;
}

/**
 * Composant SessionsManager
 *
 * @example
 * ```tsx
 * function SessionsPage() {
 *   const { user } = useAuth();
 *
 *   return (
 *     <SessionsManager
 *       userId={user.id}
 *       showRevokeAll={true}
 *       onSessionRevoked={(id) => console.log('Session révoquée:', id)}
 *     />
 *   );
 * }
 * ```
 */
export function SessionsManager({
  userId,
  onSessionRevoked,
  onAllSessionsRevoked,
  showRevokeAll = true,
  className = '',
}: SessionsManagerProps) {
  const {
    sessions,
    currentSessionId,
    isLoading,
    error,
    fetchSessions,
    revokeSession,
    revokeAllSessions,
    getCurrentSession,
    getOtherSessions,
  } = useSession();

  const { alert, showAlert, hideAlert } = useSecurityAlert();

  const [showRevokeAllConfirm, setShowRevokeAllConfirm] = useState(false);
  const [revokeAllPassword, setRevokeAllPassword] = useState('');
  const [revokingSessionId, setRevokingSessionId] = useState<string | null>(null);

  /**
   * Charger les sessions au montage
   */
  useEffect(() => {
    if (sessions.length === 0 && !isLoading) {
      fetchSessions();
    }
  }, []);

  /**
   * Écouter les notifications en temps réel via EventSource (SSE)
   */
  useEffect(() => {
    let eventSource: EventSource | null = null;

    const connectToSSE = async () => {
      try {
        // Récupérer le token d'accès via tokenManager (gère le JSON.parse)
        const token = tokenManager.getAccessToken();
        if (!token) {
          console.warn('[SSE] Token manquant, impossible de se connecter');
          return;
        }

        // Note: EventSource ne supporte pas les headers custom nativement
        // Workaround: passer le token en query param
        const url = `/api/auth/sessions/events?token=${encodeURIComponent(token)}`;
        eventSource = new EventSource(url);

        // Événement: connexion établie
        eventSource.addEventListener('connected', (_e) => {
          // SSE connecté
        });

        // Événement: session révoquée
        eventSource.addEventListener('session-revoked', (e) => {
          const data = JSON.parse(e.data);

          // Recharger la liste des sessions
          fetchSessions();

          // Afficher une alerte
          showAlert('warning', 'Une session a été révoquée sur un autre appareil');
        });

        // Événement: toutes les sessions révoquées
        eventSource.addEventListener('all-sessions-revoked', (e) => {
          const data = JSON.parse(e.data);

          // Recharger la liste des sessions
          fetchSessions();

          // Afficher une alerte
          showAlert('warning', 'Toutes les sessions ont été révoquées');
        });

        // Événement: nouvelle session créée
        eventSource.addEventListener('session-created', (e) => {
          const data = JSON.parse(e.data);

          // Recharger la liste des sessions
          fetchSessions();

          // Afficher une alerte
          showAlert('info', 'Une nouvelle session a été créée');
        });

        // Erreur de connexion
        eventSource.onerror = () => {
          eventSource?.close();
        };

      } catch {
        // Erreur de connexion SSE ignorée silencieusement
      }
    };

    // Connecter au SSE
    connectToSSE();

    // Cleanup à la destruction du composant
    return () => {
      if (eventSource) {
        eventSource.close();
      }
    };
  }, []); // Tableau vide : se connecter une seule fois au montage

  /**
   * Gérer la révocation d'une session
   */
  const handleRevokeSession = async (sessionId: string) => {
    setRevokingSessionId(sessionId);

    try {
      await revokeSession(sessionId);
      showAlert('success', 'Session révoquée avec succès');
      onSessionRevoked?.(sessionId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur de révocation';
      showAlert('error', message);
    } finally {
      setRevokingSessionId(null);
    }
  };

  /**
   * Gérer la révocation de toutes les sessions
   */
  const handleRevokeAllSessions = async () => {
    if (!revokeAllPassword) {
      showAlert('error', 'Le mot de passe est requis');
      return;
    }

    setShowRevokeAllConfirm(false);

    try {
      await revokeAllSessions(revokeAllPassword, true);
      showAlert('warning', 'Toutes les sessions ont été révoquées');
      onAllSessionsRevoked?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur de révocation globale';
      showAlert('error', message);
    }
  };

  /**
   * Rafraîchir la liste des sessions
   */
  const handleRefresh = async () => {
    try {
      await fetchSessions();
      showAlert('success', 'Sessions actualisées');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur d\'actualisation';
      showAlert('error', message);
    }
  };

  /**
   * Obtenir les sessions
   */
  const currentSession = getCurrentSession();
  const otherSessions = getOtherSessions();
const navigate = useNavigate();
  return (
    <div className={`w-full max-w-4xl ${className}`}>
      {/* Titre */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-content">
          Gestion des sessions
        </h2>
        <p className="mt-1 text-sm text-content-muted">
          Visualisez et gérez les appareils connectés à votre compte
        </p>
      </div>

      {/* Alerte */}
      {alert && (
        <div className="mb-6">
          <SecurityAlert
            {...alert}
            onClose={hideAlert}
            closable
          />
        </div>
      )}

      {/* Erreur globale */}
      {error && (
        <div
          className="mb-6 p-4 bg-danger-light border border-danger rounded-lg"
          role="alert"
        >
          <p className="text-sm text-danger">{error}</p>
        </div>
      )}

      {/* Barre d'actions */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="px-3 py-1 bg-accent-light rounded-lg">
            <span className="text-sm font-semibold text-accent">
              {sessions.length} session{sessions.length > 1 ? 's' : ''} active{sessions.length > 1 ? 's' : ''}
            </span>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-accent hover:bg-accent-light rounded-lg transition-colors disabled:opacity-50"
          >
            {isLoading ? 'Actualisation...' : 'Actualiser'}
          </button>
        </div>
      </div>

      {/* Confirmation révocation globale */}
      {showRevokeAllConfirm && (
        <div className="mb-6 bg-danger-light border border-danger rounded-lg p-4">
          <div className="flex items-start">
            <svg
              className="w-5 h-5 text-danger mt-0.5 mr-3 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-danger">
                Révoquer toutes les sessions ?
              </h4>
              <p className="mt-1 text-sm text-danger">
                Cela déconnectera tous les appareils sauf celui-ci. Vous devrez vous reconnecter sur
                chaque appareil.
              </p>
              <div className="mt-3">
                <label htmlFor="revoke-password" className="block text-sm font-medium text-danger mb-1">
                  Confirmez avec votre mot de passe
                </label>
                <input
                  id="revoke-password"
                  type="password"
                  value={revokeAllPassword}
                  onChange={(e) => setRevokeAllPassword(e.target.value)}
                  placeholder="Votre mot de passe"
                  className="w-full px-3 py-2 border border-danger rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-content"
                />
              </div>
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => { setShowRevokeAllConfirm(false); setRevokeAllPassword(''); }}
                  disabled={isLoading}
                  className="px-4 py-2 border border-border text-content-muted bg-surface hover:bg-surface-raised text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleRevokeAllSessions}
                  disabled={isLoading}
                  className="px-4 py-2 bg-danger hover:bg-danger text-content-inverted text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  {isLoading ? 'Révocation...' : 'Confirmer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chargement initial */}
      {isLoading && sessions.length === 0 && (
        <div className="text-center py-12">
          <LoadingSpinner size="lg" className="mx-auto mb-4" />
          <p className="text-sm text-content-muted">
            Chargement des sessions...
          </p>
        </div>
      )}

      {/* Liste des sessions */}
      {!isLoading && sessions.length === 0 && (
        <div className="text-center py-12 bg-surface rounded-lg border border-border">
          <svg
            className="w-12 h-12 text-content-subtle mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
          <p className="text-sm text-content-subtle">
            Aucune session active
          </p>
        </div>
      )}

      {sessions.length > 0 && (
        <div className="space-y-6">
          {/* Session actuelle */}
          {currentSession && (
            <div>
              <h3 className="text-sm font-semibold text-content-muted mb-3">
                Session actuelle
              </h3>
              <div className="bg-accent-light rounded-lg p-1">
                <SessionCard
                  session={currentSession}
                  isCurrent={true}
                />
              </div>
            </div>
          )}

          {/* Autres sessions */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-content-muted">
                Autres sessions ({otherSessions.length})
              </h3>
              {showRevokeAll && otherSessions.length > 0 && (
                <button
                  onClick={() => setShowRevokeAllConfirm(true)}
                  disabled={isLoading}
                  className="px-4 py-2 text-sm font-medium text-danger hover:bg-danger-light rounded-lg transition-colors disabled:opacity-50"
                >
                  Révoquer toutes
                </button>
              )}
            </div>
            {otherSessions.length > 0 ? (
              <div className="space-y-3">
                {otherSessions.map((session) => (
                  <SessionCard
                    key={session.id}
                    session={session}
                    isCurrent={false}
                    onRevoke={() => handleRevokeSession(session.id)}
                    isRevoking={revokingSessionId === session.id}
                  />
                ))}
              </div>
            ) : (
              <div className="bg-surface-raised rounded-lg p-4 text-center">
                <p className="text-sm text-content-subtle">
                  Aucune autre session active
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Information */}
      <div className="mt-6 bg-surface-raised border border-border rounded-lg p-4">
        <div className="flex items-start">
          <svg
            className="w-5 h-5 text-content-subtle mt-0.5 mr-3 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-content-muted">
              À propos des sessions
            </h4>
            <p className="mt-1 text-sm text-content-muted">
              Une session est créée chaque fois que vous vous connectez depuis un nouvel appareil ou
              navigateur. Si vous voyez une session que vous ne reconnaissez pas, révoquez-la
              immédiatement et changez votre mot de passe.
            </p>
          </div>
        </div>
      </div>
         {/* Navigation */}
      <div className="pt-8 text-center border-t border-border">
        <button
          onClick={() => navigate('/dashboard')}
          className="text-sm text-content-muted hover:text-content"
        >
          ← Retour au tableau de bord
        </button>
      </div>
    </div>
  );
}
